import { useEffect, useMemo, useRef, useState } from 'react';

import { ChapelProgressType } from '@/api/timetables/get-available-chapels/response';
import { GeneralElectiveProgressType } from '@/api/timetables/get-available-general-electives/response';
import { SelectedTimetableContext } from '@/components/Providers/SelectedTimetableProvider/context';
import { SelectedTimetableContextValue } from '@/components/Providers/SelectedTimetableProvider/type';
import { TimetableCourseType, TimetableTrackType, TimetableType } from '@/types/timetable';
import { PrimaryTimetableRequestType } from '@/types/timetablePayload';
import {
  DeletableCourseDtoType,
  GroupedTimetableResponseType,
  RecommendationStatusType,
} from '@/types/timetableRecommendation';
import { mergeTimetableCourses } from '@/utils/timetableSelection';

const STORAGE_KEY = 'selectedTimetable:state';
const COURSE_SELECTION_WARNING_TOAST_KEY = 'course_selection:graduation-warning-toast-shown';
const STORAGE_VERSION = 3;

type PersistedTimetableState = {
  availableChapels: TimetableCourseType[];
  availableGeneralElectiveTracks: TimetableTrackType[];
  chapelProgress: ChapelProgressType | null;
  finalizedTimetable: null | TimetableType;
  generalElectiveProgress: GeneralElectiveProgressType | null;
  groupedTimetableResponses: GroupedTimetableResponseType[];
  primaryTimetableRequest: null | PrimaryTimetableRequestType;
  recommendationStatus: null | RecommendationStatusType;
  selectedChapelCourse: null | TimetableCourseType;
  selectedGeneralElectiveCourseCodes: number[];
  selectedGeneralElectives: TimetableCourseType[];
  selectedTimetable: null | TimetableType;
  singleConflictCourses: DeletableCourseDtoType[];
};

const loadPersistedState = (): null | PersistedTimetableState => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as {
      data?: PersistedTimetableState;
      version?: number;
    };

    if (!parsed || parsed.version !== STORAGE_VERSION || !parsed.data) {
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};

export const SelectedTimetableProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const persistedStateRef = useRef<null | PersistedTimetableState>(loadPersistedState());
  const persistedState = persistedStateRef.current;

  const [primaryTimetableRequest, setPrimaryTimetableRequest] =
    useState<null | PrimaryTimetableRequestType>(
      () => persistedState?.primaryTimetableRequest ?? null,
    );
  const [recommendationStatus, setRecommendationStatus] = useState<null | RecommendationStatusType>(
    () => persistedState?.recommendationStatus ?? null,
  );
  const [groupedTimetableResponses, setGroupedTimetableResponses] = useState<
    GroupedTimetableResponseType[]
  >(() => persistedState?.groupedTimetableResponses ?? []);
  const [singleConflictCourses, setSingleConflictCourses] = useState<DeletableCourseDtoType[]>(
    () => persistedState?.singleConflictCourses ?? [],
  );

  const [availableGeneralElectiveTracks, setAvailableGeneralElectiveTracks] = useState<
    TimetableTrackType[]
  >(() => persistedState?.availableGeneralElectiveTracks ?? []);
  const [availableChapels, setAvailableChapels] = useState<TimetableCourseType[]>(
    () => persistedState?.availableChapels ?? [],
  );
  const [generalElectiveProgress, setGeneralElectiveProgress] =
    useState<GeneralElectiveProgressType | null>(
      () => persistedState?.generalElectiveProgress ?? null,
    );
  const [chapelProgress, setChapelProgress] = useState<ChapelProgressType | null>(
    () => persistedState?.chapelProgress ?? null,
  );

  const [selectedTimetable, setSelectedTimetable] = useState<null | TimetableType>(
    () => persistedState?.selectedTimetable ?? null,
  );
  const [selectedGeneralElectives, setSelectedGeneralElectives] = useState<TimetableCourseType[]>(
    () => persistedState?.selectedGeneralElectives ?? [],
  );
  const [selectedGeneralElectiveCourseCodes, setSelectedGeneralElectiveCourseCodes] = useState<
    number[]
  >(() => persistedState?.selectedGeneralElectiveCourseCodes ?? []);
  const [selectedChapelCourse, setSelectedChapelCourse] = useState<null | TimetableCourseType>(
    () => persistedState?.selectedChapelCourse ?? null,
  );
  const [finalizedTimetable, setFinalizedTimetable] = useState<null | TimetableType>(
    () => persistedState?.finalizedTimetable ?? null,
  );

  const previewTimetable = useMemo(() => {
    if (!selectedTimetable) {
      return null;
    }

    if (selectedGeneralElectives.length === 0 && !selectedChapelCourse) {
      return selectedTimetable;
    }

    return mergeTimetableCourses(selectedTimetable, selectedGeneralElectives, selectedChapelCourse);
  }, [selectedChapelCourse, selectedGeneralElectives, selectedTimetable]);

  const lastSerializedRef = useRef<null | string>(null);

  const resetSelectedTimetableState = () => {
    setPrimaryTimetableRequest(null);
    setRecommendationStatus(null);
    setGroupedTimetableResponses([]);
    setSingleConflictCourses([]);
    setAvailableGeneralElectiveTracks([]);
    setAvailableChapels([]);
    setGeneralElectiveProgress(null);
    setChapelProgress(null);
    setSelectedTimetable(null);
    setSelectedGeneralElectives([]);
    setSelectedGeneralElectiveCourseCodes([]);
    setSelectedChapelCourse(null);
    setFinalizedTimetable(null);
    lastSerializedRef.current = null;

    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(COURSE_SELECTION_WARNING_TOAST_KEY);
    } catch {
      // ignore persistence failures
    }
  };

  useEffect(() => {
    const data: PersistedTimetableState = {
      primaryTimetableRequest,
      recommendationStatus,
      groupedTimetableResponses,
      singleConflictCourses,
      availableGeneralElectiveTracks,
      availableChapels,
      generalElectiveProgress,
      chapelProgress,
      selectedTimetable,
      selectedGeneralElectives,
      selectedGeneralElectiveCourseCodes,
      selectedChapelCourse,
      finalizedTimetable,
    };

    const serialized = JSON.stringify({ version: STORAGE_VERSION, data });
    if (lastSerializedRef.current === serialized) {
      return;
    }

    lastSerializedRef.current = serialized;

    try {
      if (typeof window === 'undefined') {
        return;
      }

      window.sessionStorage.setItem(STORAGE_KEY, serialized);
    } catch {
      // ignore persistence failures
    }
  }, [
    availableChapels,
    availableGeneralElectiveTracks,
    chapelProgress,
    finalizedTimetable,
    generalElectiveProgress,
    groupedTimetableResponses,
    primaryTimetableRequest,
    recommendationStatus,
    selectedChapelCourse,
    selectedGeneralElectiveCourseCodes,
    selectedGeneralElectives,
    selectedTimetable,
    singleConflictCourses,
  ]);

  const value: SelectedTimetableContextValue = {
    resetSelectedTimetableState,
    primaryTimetableRequest,
    setPrimaryTimetableRequest,
    recommendationStatus,
    setRecommendationStatus,
    groupedTimetableResponses,
    setGroupedTimetableResponses,
    singleConflictCourses,
    setSingleConflictCourses,
    availableGeneralElectiveTracks,
    setAvailableGeneralElectiveTracks,
    availableChapels,
    setAvailableChapels,
    generalElectiveProgress,
    setGeneralElectiveProgress,
    chapelProgress,
    setChapelProgress,
    selectedTimetable,
    setSelectedTimetable,
    selectedGeneralElectives,
    setSelectedGeneralElectives,
    selectedGeneralElectiveCourseCodes,
    setSelectedGeneralElectiveCourseCodes,
    selectedChapelCourse,
    setSelectedChapelCourse,
    previewTimetable,
    finalizedTimetable,
    setFinalizedTimetable,
  };

  return (
    <SelectedTimetableContext.Provider value={value}>{children}</SelectedTimetableContext.Provider>
  );
};
